/**
 * Budgets Page
 * Create and manage annual budgets with variance tracking
 */

import React, { useState, useEffect } from 'react'
import { format } from 'date-fns'
import {
  Plus,
  Edit2,
  Trash2,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  CheckCircle2,
  RefreshCw,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { AdminUser } from '@/lib/authService'
import { formatCurrency } from '@/lib/currency'
import { supabase } from '@/lib/supabase'
import { BudgetService } from '@/services/financial/BudgetService'
import { Budget, BudgetVsActual, BudgetSummary } from '@/types/financial'

interface BudgetsProps {
  user: AdminUser | null
}

export default function Budgets({ user }: BudgetsProps) {
  const [budgets, setBudgets] = useState<Budget[]>([])
  const [loading, setLoading] = useState(false)
  const [showDialog, setShowDialog] = useState(false)
  const [selectedBudgetId, setSelectedBudgetId] = useState<string | null>(null)
  const [budgetVsActual, setBudgetVsActual] = useState<BudgetVsActual[]>([])
  const [budgetSummary, setBudgetSummary] = useState<BudgetSummary | null>(null)
  const [accounts, setAccounts] = useState<any[]>([])

  const budgetService = new BudgetService(supabase)

  const [formData, setFormData] = useState({
    budget_name: '',
    budget_year: new Date().getFullYear(),
    start_date: format(new Date(new Date().getFullYear(), 0, 1), 'yyyy-MM-dd'),
    end_date: format(new Date(new Date().getFullYear(), 11, 31), 'yyyy-MM-dd'),
    notes: '',
  })

  // Load budgets on mount
  useEffect(() => {
    if (user?.churchId) {
      loadBudgets()
      loadAccounts()
    }
  }, [user?.churchId])

  // Load budget details when one is selected
  useEffect(() => {
    if (selectedBudgetId) {
      loadBudgetDetails(selectedBudgetId)
    }
  }, [selectedBudgetId])

  async function loadBudgets() {
    if (!user?.churchId) return

    setLoading(true)
    try {
      const result = await budgetService.getChurchBudgets(user.churchId)

      if (result.success && result.data) {
        setBudgets(result.data)

        // Auto-select active budget
        const activeBudget = result.data.find((b: Budget) => b.status === 'active')
        if (activeBudget && !selectedBudgetId) {
          setSelectedBudgetId(activeBudget.id)
        }
      } else {
        alert(`Error: ${result.error?.message || 'Failed to load budgets'}`)
      }
    } catch (error) {
      console.error('Error loading budgets:', error)
      alert('Error: An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  async function loadBudgetDetails(budgetId: string) {
    setLoading(true)
    try {
      // Load budget vs actual
      const vsActualResult = await budgetService.getBudgetVsActual(budgetId)
      if (vsActualResult.success && vsActualResult.data) {
        setBudgetVsActual(vsActualResult.data)
      }

      // Load budget summary
      const summaryResult = await budgetService.getBudgetSummary(budgetId)
      if (summaryResult.success && summaryResult.data) {
        setBudgetSummary(summaryResult.data)
      }
    } catch (error) {
      console.error('Error loading budget details:', error)
    } finally {
      setLoading(false)
    }
  }

  async function loadAccounts() {
    if (!user?.churchId) return

    try {
      const { data, error } = await supabase
        .from('chart_of_accounts')
        .select('id, account_code, account_name, account_type')
        .eq('church_id', user.churchId)
        .eq('is_active', true)
        .in('account_type', ['revenue', 'expense'])
        .order('account_code')

      if (error) throw error
      setAccounts(data || [])
    } catch (error) {
      console.error('Error loading accounts:', error)
    }
  }

  async function handleRefreshActuals(budgetId: string) {
    setLoading(true)
    try {
      const result = await budgetService.updateBudgetActuals(budgetId)

      if (result.success) {
        alert('Success: Budget actuals updated from general ledger')
        loadBudgetDetails(budgetId)
      } else {
        alert(`Error: ${result.error?.message || 'Failed to update actuals'}`)
      }
    } catch (error) {
      console.error('Error refreshing actuals:', error)
      alert('Error: An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  async function handleActivateBudget(budgetId: string) {
    if (!user?.id) return

    setLoading(true)
    try {
      const result = await budgetService.activateBudget(budgetId, user.id)

      if (result.success) {
        alert('Success: Budget activated successfully')
        loadBudgets()
      } else {
        alert(`Error: ${result.error?.message || 'Failed to activate budget'}`)
      }
    } catch (error) {
      console.error('Error activating budget:', error)
      alert('Error: An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  function openDialog(budget?: Budget) {
    if (budget) {
      setFormData({
        budget_name: budget.budget_name,
        budget_year: budget.budget_year,
        start_date: budget.start_date.toString().split('T')[0],
        end_date: budget.end_date.toString().split('T')[0],
        notes: budget.notes || '',
      })
    } else {
      setFormData({
        budget_name: '',
        budget_year: new Date().getFullYear(),
        start_date: format(new Date(new Date().getFullYear(), 0, 1), 'yyyy-MM-dd'),
        end_date: format(new Date(new Date().getFullYear(), 11, 31), 'yyyy-MM-dd'),
        notes: '',
      })
    }
    setShowDialog(true)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    alert('Coming Soon: Budget creation form with account line items will be implemented next')
    setShowDialog(false)
  }

  function getVarianceBadge(percent: number) {
    if (Math.abs(percent) <= 5) {
      return (
        <Badge variant="default" className="gap-1">
          <CheckCircle2 className="h-3 w-3" />
          On Track
        </Badge>
      )
    } else if (Math.abs(percent) <= 15) {
      return (
        <Badge variant="secondary" className="gap-1">
          <AlertCircle className="h-3 w-3" />
          Watch
        </Badge>
      )
    } else {
      return (
        <Badge variant="destructive" className="gap-1">
          <AlertCircle className="h-3 w-3" />
          {percent > 0 ? 'Over Budget' : 'Under Budget'}
        </Badge>
      )
    }
  }

  const selectedBudget = budgets.find(b => b.id === selectedBudgetId)

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Budgets</h1>
          <p className="text-muted-foreground">
            Create and manage annual budgets with variance tracking
          </p>
        </div>
        <div className="flex gap-2">
          {selectedBudgetId && (
            <Button
              variant="outline"
              onClick={() => handleRefreshActuals(selectedBudgetId)}
              disabled={loading}
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh Actuals
            </Button>
          )}
          <Button onClick={() => openDialog()}>
            <Plus className="mr-2 h-4 w-4" />
            Create Budget
          </Button>
        </div>
      </div>

      {/* Budget Selector */}
      {budgets.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Select Budget</CardTitle>
            <CardDescription>Choose a budget to view details</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              {budgets.map((budget) => (
                <Button
                  key={budget.id}
                  variant={selectedBudgetId === budget.id ? 'default' : 'outline'}
                  onClick={() => setSelectedBudgetId(budget.id)}
                >
                  {budget.budget_name}
                  {budget.status === 'active' && (
                    <Badge variant="secondary" className="ml-2">Active</Badge>
                  )}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Budget Summary */}
      {budgetSummary && selectedBudget && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{budgetSummary.budget_name}</CardTitle>
                <CardDescription>
                  {format(new Date(selectedBudget.start_date), 'MMM dd, yyyy')} -{' '}
                  {format(new Date(selectedBudget.end_date), 'MMM dd, yyyy')}
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Badge variant={selectedBudget.status === 'active' ? 'default' : 'outline'}>
                  {selectedBudget.status}
                </Badge>
                {selectedBudget.status === 'draft' && (
                  <Button
                    size="sm"
                    onClick={() => handleActivateBudget(selectedBudget.id)}
                  >
                    Activate Budget
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Summary Totals */}
              <div className="grid gap-4 md:grid-cols-4 border-b pb-4">
                <div>
                  <div className="text-sm text-muted-foreground">Total Budgeted</div>
                  <div className="text-2xl font-bold">
                    {formatCurrency(budgetSummary.total_budgeted, 'GHS')}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Total Actual</div>
                  <div className="text-2xl font-bold">
                    {formatCurrency(budgetSummary.total_actual, 'GHS')}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Variance</div>
                  <div className={`text-2xl font-bold ${budgetSummary.total_variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {budgetSummary.total_variance >= 0 ? '+' : ''}
                    {formatCurrency(budgetSummary.total_variance, 'GHS')}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Variance %</div>
                  <div className={`text-2xl font-bold ${Math.abs(budgetSummary.variance_percent) > 10 ? 'text-orange-600' : 'text-gray-600'}`}>
                    {budgetSummary.variance_percent >= 0 ? '+' : ''}
                    {budgetSummary.variance_percent.toFixed(1)}%
                  </div>
                </div>
              </div>

              {/* Account Status */}
              <div className="grid gap-4 md:grid-cols-3">
                <div className="text-center p-4 border rounded-lg">
                  <div className="text-3xl font-bold text-green-600">{budgetSummary.accounts_on_track}</div>
                  <div className="text-sm text-muted-foreground">On Track</div>
                </div>
                <div className="text-center p-4 border rounded-lg">
                  <div className="text-3xl font-bold text-orange-600">{budgetSummary.accounts_over_budget}</div>
                  <div className="text-sm text-muted-foreground">Over Budget</div>
                </div>
                <div className="text-center p-4 border rounded-lg">
                  <div className="text-3xl font-bold text-blue-600">{budgetSummary.accounts_under_budget}</div>
                  <div className="text-sm text-muted-foreground">Under Budget</div>
                </div>
              </div>

              {/* Budget Lines */}
              {budgetVsActual.length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Account</TableHead>
                      <TableHead className="text-right">Budgeted</TableHead>
                      <TableHead className="text-right">Actual</TableHead>
                      <TableHead className="text-right">Variance</TableHead>
                      <TableHead className="text-right">% Variance</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {budgetVsActual.map((line) => (
                      <TableRow key={line.line_item_id}>
                        <TableCell className="font-medium">
                          {line.account_code} - {line.account_name}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(line.budgeted_amount, 'GHS')}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(line.actual_amount, 'GHS')}
                        </TableCell>
                        <TableCell
                          className={`text-right font-mono ${
                            line.variance >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}
                        >
                          {line.variance >= 0 ? '+' : ''}
                          {formatCurrency(Math.abs(line.variance), 'GHS')}
                        </TableCell>
                        <TableCell
                          className={`text-right ${
                            Math.abs(line.variance_percent) > 10
                              ? 'font-semibold text-orange-600'
                              : ''
                          }`}
                        >
                          {line.variance_percent >= 0 ? '+' : ''}
                          {line.variance_percent.toFixed(1)}%
                        </TableCell>
                        <TableCell>{getVarianceBadge(line.variance_percent)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {budgets.length === 0 && !loading && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Budgets Created</h3>
            <p className="text-muted-foreground text-center mb-4">
              Create your first budget to start tracking spending against your financial plan
            </p>
            <Button onClick={() => openDialog()}>
              <Plus className="mr-2 h-4 w-4" />
              Create Your First Budget
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Create New Budget
            </DialogTitle>
            <DialogDescription>
              Set up your annual budget with line items for each account
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="budget_name">
                  Budget Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="budget_name"
                  value={formData.budget_name}
                  onChange={(e) => setFormData({ ...formData, budget_name: e.target.value })}
                  placeholder="e.g., 2026 Annual Budget"
                  required
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="budget_year">Budget Year</Label>
                  <Input
                    id="budget_year"
                    type="number"
                    value={formData.budget_year}
                    onChange={(e) =>
                      setFormData({ ...formData, budget_year: parseInt(e.target.value) })
                    }
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="start_date">Start Date</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="end_date">End Date</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  placeholder="Optional notes about this budget..."
                />
              </div>

              <div className="border-t pt-4">
                <p className="text-sm text-muted-foreground">
                  <strong>Next Step:</strong> After creating the budget, you'll be able to add line items for each account with budgeted amounts and optional monthly breakdown.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">
                Create Budget
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
